import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Rocket, Loader2, ArrowRight } from "lucide-react";
import { toast } from "sonner";

interface Props {
  ideaId: string;
  ownerUserId: string;
  currentUserId: string | null;
  hasGrantBrief: boolean;
}

const PromoteToProjectButton = ({ ideaId, ownerUserId, currentUserId, hasGrantBrief }: Props) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);

  const { data: project } = useQuery({
    queryKey: ["project-by-idea", ideaId, currentUserId],
    queryFn: async () => {
      if (!currentUserId) return null;
      const { data } = await supabase
        .from("projects")
        .select("id")
        .eq("idea_id", ideaId)
        .eq("user_id", currentUserId)
        .maybeSingle();
      return data;
    },
    enabled: !!currentUserId && currentUserId === ownerUserId,
  });

  const promote = useMutation({
    mutationFn: async () => {
      if (!currentUserId) throw new Error("Not signed in");
      const { data: details } = await supabase
        .from("idea_grant_details")
        .select("id")
        .eq("idea_id", ideaId)
        .maybeSingle();
      const { data: created, error } = await supabase
        .from("projects")
        .insert({
          user_id: currentUserId,
          idea_id: ideaId,
          grant_details_id: details?.id ?? null,
        })
        .select()
        .single();
      if (error) throw error;
      // Kick off the initial overall score (don't await failures hard)
      try {
        await supabase.functions.invoke("score-project", {
          body: { project_id: created.id, mode: "overall" },
        });
      } catch (e) {
        console.warn("Initial score failed:", e);
      }
      return created;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ["project-by-idea", ideaId] });
      toast.success("Project created. Generating your readiness score…");
      navigate(`/projects/${created.id}`);
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not create project"),
    onSettled: () => setBusy(false),
  });

  if (!currentUserId || currentUserId !== ownerUserId) return null;

  if (project) {
    return (
      <Button
        variant="outline"
        className="gap-2"
        onClick={() => navigate(`/projects/${project.id}`)}
      >
        Open Project <ArrowRight className="h-4 w-4" />
      </Button>
    );
  }

  if (!hasGrantBrief) {
    return (
      <Button variant="outline" disabled className="gap-2" title="Add a Grant-Ready brief first">
        <Rocket className="h-4 w-4" /> Promote to Project
      </Button>
    );
  }

  return (
    <Button
      className="gap-2"
      disabled={busy || promote.isPending}
      onClick={() => {
        setBusy(true);
        promote.mutate();
      }}
    >
      {promote.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />}
      Promote to Project
    </Button>
  );
};

export default PromoteToProjectButton;